import { useMutation, useQueryClient } from '@tanstack/react-query';
import QualAPI from 'utils/QualAPI';
import { codeKeys } from '../queries';
import { normalizeId } from '../../utils/idNormalization';
import { createCodeCacheUtils } from '../../utils/codeCacheUtils';
import { useOptimisticPromise } from 'hooks/optimistic/useOptimisticPromise';

/**
 * Hook for creating a new code with optimistic updates
 * @returns {Object} The mutation object with mutate function and state
 */
export function useCreateCode() {
  const queryClient = useQueryClient();
  const cacheUtils = createCodeCacheUtils(queryClient);
  const { setPromise } = useOptimisticPromise();
  
  return useMutation({
    mutationFn: (codeData) => {
      const promise = QualAPI.addCode(codeData).then(response => response.data);
      
      // Store the promise using the client_id
      setPromise(codeData.client_id, promise);
      
      return promise;
    },
    
    onMutate: async (variables) => {
      const { projectId, name, client_id, ...otherData } = variables;
      const normalizedProjectId = normalizeId(projectId);

      // Cancel any outgoing queries for the project codes list
      await queryClient.cancelQueries({ queryKey: codeKeys.list(normalizedProjectId) });
      
      // Get current project codes data
      const previousProjectCodes = queryClient.getQueryData(codeKeys.list(normalizedProjectId));
      
      // Create optimistic new code using client_id
      const optimisticCode = {
        id: client_id, // Use client_id instead of uuidv4()
        name,
        position: 0,
        parent_id: null,
        project_id: normalizedProjectId,
        synthesis: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        color: 'grey',
        ...otherData
      };
      
      // Only update the list cache if we have previous data
      if (previousProjectCodes?.codes) {
        // Update positions of existing codes with no parent (add 1 to their position)
        const updatedCodes = previousProjectCodes.codes.map(code => {
          if (code.parent_id === null || code.parent_id === undefined) {
            return {
              ...code,
              position: (code.position || 0) + 1
            };
          }
          return code;
        });
        
        // Add the new code at the beginning
        const newCodesList = [optimisticCode, ...updatedCodes];
        
        // Update the cache optimistically
        queryClient.setQueryData(codeKeys.list(normalizedProjectId), {
          ...previousProjectCodes,
          codes: newCodesList
        });
      }
      
      queryClient.setQueryData(codeKeys.detail(client_id), optimisticCode);
      
      return { previousProjectCodes, projectId: normalizedProjectId, optimisticCodeId: client_id };
    },
    
    onSuccess: (data, variables, context) => {
      const { projectId, optimisticCodeId } = context;
      const { code: actualCode } = data;

      if (actualCode?.id && optimisticCodeId) {
        // Use cache utils to properly resolve the optimistic ID
        cacheUtils.resolveOptimisticId(optimisticCodeId, actualCode, projectId);
      } else {
        // Fallback: just invalidate everything if we don't have proper data
        queryClient.removeQueries({ queryKey: codeKeys.list(projectId) });
        queryClient.invalidateQueries({ queryKey: codeKeys.list(projectId) });
      }
    },
    
    onError: (err, variables, context) => {
      const { projectId, client_id } = variables;
      const normalizedProjectId = normalizeId(projectId);
      
      // Just invalidate all related caches and let them refetch
      queryClient.invalidateQueries({ queryKey: codeKeys.list(normalizedProjectId) });
      if (context?.optimisticCodeId) {
        queryClient.invalidateQueries({ queryKey: codeKeys.detail(context.optimisticCodeId) });
      }
      
      console.error('Error creating code:', err);
    }
  });
}

// Export all code-related mutations
export const codeMutations = {
  useCreateCode
};